import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Languages } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr, adminDelete } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { PRIORITY_STYLE } from "../../lib/constants";
import { useLang } from "../../context/LanguageContext";
import { priorityLabel } from "../../lib/statusI18n";

// Page Demandes épurée : la liste s'affiche INSTANTANÉMENT (dernière liste connue
// affichée immédiatement depuis le cache navigateur, puis rafraîchie en silence).
// Plus de filtres au-dessus ; clic sur n'importe quelle ligne = ouverture du dossier.
const CACHE_KEY = "aca_admin_projects_cache_v2"; // v2 : ignore l'ancien cache éventuellement corrompu

export default function AdminProjects() {
  const nav = useNavigate();
  const { tf: t } = useLang();
  const [projects, setProjects] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "[]"); } catch { return []; }
  });
  const [loading, setLoading] = useState(() => !localStorage.getItem(CACHE_KEY));
  const [loadError, setLoadError] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Auto-réparation : si le serveur est en cours de redémarrage (mise en ligne),
  // on réessaie silencieusement pendant ~3 minutes (durée typique d'un déploiement).
  // La liste déjà connue reste affichée — plus jamais de page bloquée à rouvrir à la main.
  const RETRY_DELAYS = [3, 4, 5, 6, 8, 10, 12, 15, 15, 20, 20, 20, 25, 30, 30]; // ~3 min au total
  const load = (attempt = 0) => {
    api.get("/admin/projects")
      .then((r) => {
        setProjects(r.data);
        setLoadError(false);
        setRetrying(false);
        setLoading(false);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(r.data)); } catch { /* quota */ }
      })
      .catch((e) => {
        const status = e?.response?.status || (e?.code === "ECONNABORTED" ? "délai dépassé" : "serveur injoignable");
        if (attempt < RETRY_DELAYS.length) {
          setRetrying(true);
          setLoading(false);
          setTimeout(() => load(attempt + 1), RETRY_DELAYS[attempt] * 1000);
        } else {
          setRetrying(false);
          setLoadError(String(status));
          setLoading(false);
        }
      });
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (e, p) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer définitivement le dossier ${p.ref} — « ${p.app_name || p.title} » ? Cette action est irréversible.`)) return;
    try { if (!await adminDelete(`/admin/projects/${p.id}`)) return; toast.success("Dossier supprimé."); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div className="mx-auto max-w-7xl">
      <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">{t("apj_kicker")}</div>
      <h1 className="mt-3 font-display text-3xl font-900 text-white">{t("apj_title")}</h1>

      {/* Liste directe, sans filtres : clic sur une ligne = ouvrir la demande. */}
      <div data-testid="projects-panel-demandes">
      <div className="mt-6 overflow-x-auto border border-white/10">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead className="border-b border-white/10 bg-surface text-[11px] uppercase tracking-wider text-ink-tertiary">
            <tr>
              <th className="p-4">{t("apj_th_ref")}</th><th className="p-4">{t("apj_th_project")}</th><th className="p-4">{t("apj_th_candidate")}</th>
              <th className="p-4">{t("apj_th_priority")}</th><th className="p-4">{t("apj_th_category")}</th><th className="p-4">{t("apj_th_status")}</th><th className="p-4">Suivi de production</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.id} onClick={() => nav(`/admin/projets/${p.id}`)} data-testid={`admin-project-${p.id}`}
                className="cursor-pointer border-b border-white/5 transition-colors hover:bg-cyan/5"
                title={`Ouvrir le dossier ${p.ref}`}>
                <td className="p-4 text-cyan">{p.ref}</td>
                <td className="p-4 text-white">
                  {p.app_name || p.title_fr || p.title}
                  {p.lang && p.lang !== "fr" && (
                    <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-cyan/30 bg-cyan/10 px-2 py-0.5 align-middle text-[9px] font-700 uppercase tracking-wider text-cyan"
                      title={`Demande rédigée en ${p.lang.toUpperCase()} — affichée en français (traduction automatique)`}
                      data-testid={`admin-project-translated-${p.id}`}>
                      <Languages size={10} /> {p.lang}→FR
                    </span>
                  )}
                </td>
                <td className="p-4"><div className="text-white">{p.author}</div><div className="text-xs text-ink-tertiary">{p.author_country}</div></td>
                <td className="p-4">
                  {p.priority ? <span className={`border px-2 py-0.5 text-[10px] font-600 uppercase tracking-wider ${PRIORITY_STYLE[p.priority]}`}>{priorityLabel(t, p.priority)}</span> : <span className="text-ink-tertiary">—</span>}
                </td>
                <td className="p-4 text-ink-secondary">{p.category || "—"}</td>
                <td className="p-4"><StatusBadge status={p.status} /></td>
                <td className="p-4" data-testid={`admin-project-tracking-${p.id}`}>
                  {p.status === "SÉLECTIONNÉE" ? (
                    <span className="block min-w-[130px]">
                      <span className="text-xs font-600 text-cyan">{p.prod_status || "Sélectionné"}</span>
                      <span className="mt-1 flex items-center gap-2">
                        <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
                          <span className="block h-full rounded-full bg-cyan" style={{ width: `${p.progress || 0}%` }} />
                        </span>
                        <span className="text-[10px] tabular-nums text-ink-secondary">{p.progress || 0}%</span>
                      </span>
                    </span>
                  ) : <span className="text-xs text-ink-tertiary">—</span>}
                </td>
                <td className="p-4 text-right">
                  <button onClick={(e) => remove(e, p)} data-testid={`admin-project-delete-${p.id}`} title="Supprimer le dossier"
                    className="inline-flex items-center justify-center rounded-full border border-white/15 p-2 text-ink-tertiary transition-colors hover:border-red-500/50 hover:text-red-400">
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {retrying && (
          <div className="border-b border-white/10 bg-cyan/[0.06] px-4 py-2.5 text-center text-xs text-cyan" data-testid="admin-projects-retrying">
            Le serveur redémarre (mise en ligne en cours) — nouvelle tentative automatique dans quelques secondes…
          </div>
        )}
        {loadError && (
          <div className="p-12 text-center" data-testid="admin-projects-error">
            <p className="text-sm text-amber-300">
              Le chargement des demandes a échoué (code : {loadError}) — le serveur était momentanément
              indisponible (par exemple pendant une mise en ligne). Vos dossiers ne sont pas perdus.
            </p>
            <button onClick={() => { setLoadError(false); setLoading(true); load(); }} data-testid="admin-projects-retry"
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-cyan/40 bg-cyan/10 px-5 py-2.5 text-sm font-600 text-cyan transition-colors hover:bg-cyan/20">
              Réessayer
            </button>
          </div>
        )}
        {!loadError && loading && (
          <div className="p-12 text-center text-sm text-ink-secondary" data-testid="admin-projects-loading">Chargement des demandes…</div>
        )}
        {/* « Aucune demande » ne s'affiche que si le serveur a répondu avec une liste VRAIMENT vide
            (jamais pendant un réessai automatique). */}
        {!loadError && !loading && !retrying && projects.length === 0 && <div className="p-12 text-center text-ink-secondary" data-testid="admin-projects-empty">{t("apj_empty")}</div>}
        {!loadError && !loading && retrying && projects.length === 0 && (
          <div className="p-12 text-center text-sm text-ink-secondary" data-testid="admin-projects-waiting">La liste arrive dès que le serveur est de retour…</div>
        )}
      </div>
      </div>
    </div>
  );
}
